import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  SparklesIcon,
  CheckIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

const CATEGORIES = [
  { value: 'getting_started', label: 'Getting Started' },
  { value: 'data_sources', label: 'Data Sources' },
  { value: 'dashboards', label: 'Dashboards' },
  { value: 'ai_assistant', label: 'AI Assistant' },
  { value: 'billing', label: 'Billing' },
  { value: 'troubleshooting', label: 'Troubleshooting' }
];

function DocEditorModal({ isOpen, onClose, doc, onSave }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'getting_started',
    content: '',
    excerpt: '',
    searchKeywords: '',
    order: 0,
    isPublished: false
  });
  
  useEffect(() => {
    if (doc) {
      setFormData({
        title: doc.title || '',
        slug: doc.slug || '',
        category: doc.category || 'getting_started',
        content: doc.content || '',
        excerpt: doc.excerpt || '',
        searchKeywords: doc.searchKeywords?.join(', ') || '',
        order: doc.order || 0,
        isPublished: doc.isPublished || false
      });
    } else {
      setFormData({
        title: '',
        slug: '',
        category: 'getting_started',
        content: '',
        excerpt: '',
        searchKeywords: '',
        order: 0,
        isPublished: false
      });
    }
  }, [doc, isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        ...formData,
        searchKeywords: formData.searchKeywords
          .split(',')
          .map(k => k.trim().toLowerCase())
          .filter(k => k)
      };
      
      if (doc?._id) {
        await adminApi.updateHelpDoc(doc._id, data);
        toast.success('Document updated');
      } else {
        await adminApi.createHelpDoc(data);
        toast.success('Document created');
      }
      
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };
  
  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData(f => ({ ...f, slug }));
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              {doc ? 'Edit Document' : 'Create New Document'}
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                  className="input w-full"
                  placeholder="Document title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Slug
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(f => ({ ...f, slug: e.target.value }))}
                    className="input flex-1"
                    placeholder="url-friendly-slug"
                  />
                  <button
                    type="button"
                    onClick={generateSlug}
                    className="btn-ghost text-sm"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(f => ({ ...f, category: e.target.value }))}
                  className="input w-full"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                  className="input w-full"
                  min="0"
                />
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData(f => ({ ...f, isPublished: e.target.checked }))}
                    className="w-4 h-4 rounded border-surface-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-surface-300">Published</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Excerpt
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData(f => ({ ...f, excerpt: e.target.value }))}
                className="input w-full h-20 resize-none"
                placeholder="Brief description (auto-generated from content if left empty)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Content * (Markdown)
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(f => ({ ...f, content: e.target.value }))}
                className="input w-full h-64 resize-none font-mono text-sm"
                placeholder="# Heading&#10;&#10;Your content here using **Markdown** formatting..."
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Search Keywords (comma-separated)
              </label>
              <input
                type="text"
                value={formData.searchKeywords}
                onChange={(e) => setFormData(f => ({ ...f, searchKeywords: e.target.value }))}
                className="input w-full"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
            
            <div className="flex gap-3 pt-4 border-t border-surface-700">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Saving...' : doc ? 'Update Document' : 'Create Document'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// AI Assistant Modal for generating documentation
function AIAssistantModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState('analyze'); // 'analyze', 'review', 'generating'
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState({});
  const [customDoc, setCustomDoc] = useState({ title: '', category: 'getting_started', description: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentTitle: '' });
  const [existingInfo, setExistingInfo] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      setStep('analyze');
      setSuggestions([]);
      setSelectedDocs({});
      setProgress({ current: 0, total: 0, currentTitle: '' });
    }
  }, [isOpen]);
  
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await adminApi.analyzeDocumentation();
      setSuggestions(response.data.suggestions || []);
      setExistingInfo({
        count: response.data.existingDocs,
        byCategory: response.data.existingByCategory
      });
      
      // Pre-select all suggestions
      const selected = {};
      response.data.suggestions?.forEach((s, idx) => {
        selected[idx] = true;
      });
      setSelectedDocs(selected);
      
      setStep('review');
    } catch (error) {
      toast.error('Failed to analyze documentation');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const handleAddCustomDoc = () => {
    if (!customDoc.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    const newSuggestion = {
      title: customDoc.title,
      category: customDoc.category,
      description: customDoc.description,
      priority: 'medium',
      isCustom: true
    };
    
    setSuggestions(prev => [...prev, newSuggestion]);
    setSelectedDocs(prev => ({ ...prev, [suggestions.length]: true }));
    setCustomDoc({ title: '', category: 'getting_started', description: '' });
    setShowAddForm(false);
  };
  
  const handleGenerate = async () => {
    const docsToCreate = suggestions.filter((_, idx) => selectedDocs[idx]);
    
    if (docsToCreate.length === 0) {
      toast.error('Please select at least one document to create');
      return;
    }
    
    setStep('generating');
    setGenerating(true);
    setProgress({ current: 0, total: docsToCreate.length, currentTitle: '' });
    
    try {
      // Generate in batches of 3 to avoid timeout
      const batchSize = 3;
      let created = 0;
      let failed = 0;
      
      for (let i = 0; i < docsToCreate.length; i += batchSize) {
        const batch = docsToCreate.slice(i, i + batchSize);
        setProgress({ 
          current: i, 
          total: docsToCreate.length, 
          currentTitle: batch.map(d => d.title).join(', ')
        });
        
        const response = await adminApi.generateHelpDocsBatch(batch);
        created += response.data.created || 0;
        failed += response.data.failed || 0;
      }
      
      setProgress({ current: docsToCreate.length, total: docsToCreate.length, currentTitle: '' });
      
      if (created > 0) {
        toast.success(`Created ${created} document${created > 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}`);
        onSuccess();
        onClose();
      } else {
        toast.error('Failed to create documents');
      }
    } catch (error) {
      toast.error('Failed to generate documents');
    } finally {
      setGenerating(false);
    }
  };
  
  const selectedCount = Object.values(selectedDocs).filter(Boolean).length;
  
  return (
    <Dialog open={isOpen} onClose={generating ? () => {} : onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100 flex items-center gap-2">
              <SparklesIcon className="w-6 h-6 text-purple-400" />
              AI Documentation Assistant
            </Dialog.Title>
            {!generating && (
              <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Step: Analyze */}
          {step === 'analyze' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <SparklesIcon className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-surface-100 mb-2">
                Analyze Your Documentation
              </h3>
              <p className="text-surface-400 mb-6 max-w-md mx-auto">
                The AI will analyze your existing documentation and suggest missing topics that users would find helpful.
              </p>
              <button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="btn-primary"
              >
                {analyzing ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5" />
                    Analyze Documentation
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Step: Review */}
          {step === 'review' && (
            <div>
              {existingInfo && (
                <div className="bg-surface-800 rounded-lg p-4 mb-4">
                  <p className="text-sm text-surface-300">
                    <span className="font-medium text-surface-100">{existingInfo.count}</span> existing documents found
                  </p>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-surface-100">
                  Suggested Documents ({suggestions.length})
                </h3>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="btn-ghost text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Custom
                </button>
              </div>
              
              {/* Add Custom Form */}
              {showAddForm && (
                <div className="bg-surface-800 rounded-lg p-4 mb-4 space-y-3">
                  <input
                    type="text"
                    value={customDoc.title}
                    onChange={(e) => setCustomDoc(c => ({ ...c, title: e.target.value }))}
                    placeholder="Document title"
                    className="input w-full"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={customDoc.category}
                      onChange={(e) => setCustomDoc(c => ({ ...c, category: e.target.value }))}
                      className="input"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={customDoc.description}
                      onChange={(e) => setCustomDoc(c => ({ ...c, description: e.target.value }))}
                      placeholder="Brief description (optional)"
                      className="input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddForm(false)} className="btn-ghost text-sm">
                      Cancel
                    </button>
                    <button onClick={handleAddCustomDoc} className="btn-primary text-sm">
                      Add to List
                    </button>
                  </div>
                </div>
              )}
              
              {/* Suggestions List */}
              {suggestions.length === 0 ? (
                <div className="text-center py-8 text-surface-400">
                  <ListBulletIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No suggestions found. Your documentation looks comprehensive!</p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="btn-ghost mt-4"
                  >
                    Add a custom document
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedDocs[idx] 
                          ? 'bg-purple-500/10 border border-purple-500/30' 
                          : 'bg-surface-800 hover:bg-surface-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDocs[idx] || false}
                        onChange={(e) => setSelectedDocs(prev => ({ ...prev, [idx]: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-surface-600 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-surface-100">{suggestion.title}</span>
                          {suggestion.isCustom && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Custom</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            suggestion.priority === 'high' 
                              ? 'bg-red-500/20 text-red-400'
                              : suggestion.priority === 'low'
                                ? 'bg-surface-600 text-surface-400'
                                : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {suggestion.priority}
                          </span>
                        </div>
                        <p className="text-sm text-surface-400 mt-1">{suggestion.description}</p>
                        <span className="text-xs text-surface-500 capitalize">
                          {suggestion.category?.replace('_', ' ')}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-4 mt-4 border-t border-surface-700">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={selectedCount === 0}
                  className="btn-primary flex-1"
                >
                  <SparklesIcon className="w-5 h-5" />
                  Create {selectedCount} Document{selectedCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}
          
          {/* Step: Generating */}
          {step === 'generating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowPathIcon className="w-8 h-8 text-purple-400 animate-spin" />
              </div>
              <h3 className="text-lg font-medium text-surface-100 mb-2">
                Generating Documentation
              </h3>
              <p className="text-surface-400 mb-4">
                Creating {progress.total} document{progress.total !== 1 ? 's' : ''}...
              </p>
              
              {/* Progress bar */}
              <div className="w-full bg-surface-700 rounded-full h-2 mb-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-surface-500">
                {progress.current} of {progress.total} completed
              </p>
              {progress.currentTitle && (
                <p className="text-xs text-surface-500 mt-2 truncate">
                  Creating: {progress.currentTitle}
                </p>
              )}
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default function Documentation() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', isPublished: '' });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  
  useEffect(() => {
    loadDocuments();
  }, [filter]);
  
  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.isPublished) params.isPublished = filter.isPublished;
      
      const response = await adminApi.getHelpDocs(params);
      setDocuments(response.data.documents);
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditorOpen(true);
  };
  
  const handleCreate = () => {
    setEditingDoc(null);
    setEditorOpen(true);
  };
  
  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    
    try {
      await adminApi.deleteHelpDoc(doc._id);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };
  
  const handleTogglePublish = async (doc) => {
    try {
      await adminApi.updateHelpDoc(doc._id, { isPublished: !doc.isPublished });
      toast.success(doc.isPublished ? 'Document unpublished' : 'Document published');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };
  
  const publishedCount = documents.filter(d => d.isPublished).length;
  const draftCount = documents.filter(d => !d.isPublished).length;
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Help Documentation</h1>
          <p className="text-surface-400 mt-1">
            {publishedCount} published, {draftCount} drafts
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setAiAssistantOpen(true)} 
            className="btn-secondary bg-purple-500/10 border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
          >
            <SparklesIcon className="w-5 h-5" />
            AI Assistant
          </button>
          <button onClick={handleCreate} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
            New Document
          </button>
        </div>
      </div>
      
      {/* Stats by Category */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {categories.map(cat => (
          <button
            key={cat.value}
            onClick={() => setFilter(f => ({ 
              ...f, 
              category: f.category === cat.value ? '' : cat.value 
            }))}
            className={`card hover:border-primary-500 transition-colors ${
              filter.category === cat.value ? 'border-primary-500' : ''
            }`}
          >
            <p className="text-xl font-bold text-surface-100">{cat.count}</p>
            <p className="text-xs text-surface-400">{cat.label}</p>
          </button>
        ))}
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <select
            value={filter.category}
            onChange={(e) => setFilter(f => ({ ...f, category: e.target.value }))}
            className="input"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          
          <select
            value={filter.isPublished}
            onChange={(e) => setFilter(f => ({ ...f, isPublished: e.target.value }))}
            className="input"
          >
            <option value="">All Status</option>
            <option value="true">Published</option>
            <option value="false">Drafts</option>
          </select>
          
          {(filter.category || filter.isPublished) && (
            <button
              onClick={() => setFilter({ category: '', isPublished: '' })}
              className="btn-ghost"
            >
              Clear Filters
            </button>
          )}
          
          <button onClick={loadDocuments} className="btn-ghost ml-auto">
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Documents Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Category</th>
              <th>Status</th>
              <th>Order</th>
              <th>Views</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-surface-400">
                  No documents found
                </td>
              </tr>
            ) : (
              documents.map(doc => (
                <tr key={doc._id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="w-5 h-5 text-surface-400" />
                      <div>
                        <p className="font-medium text-surface-100">{doc.title}</p>
                        <p className="text-xs text-surface-500">/{doc.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge-neutral capitalize">
                      {doc.category?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleTogglePublish(doc)}
                      className={`flex items-center gap-1 text-sm ${
                        doc.isPublished ? 'text-emerald-400' : 'text-surface-500'
                      }`}
                    >
                      {doc.isPublished ? (
                        <>
                          <EyeIcon className="w-4 h-4" />
                          Published
                        </>
                      ) : (
                        <>
                          <EyeSlashIcon className="w-4 h-4" />
                          Draft
                        </>
                      )}
                    </button>
                  </td>
                  <td className="text-surface-400">{doc.order}</td>
                  <td className="text-surface-400">{doc.viewCount || 0}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(doc)}
                        className="btn-ghost btn-sm"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="btn-ghost btn-sm text-red-400 hover:bg-red-900/20"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Editor Modal */}
      <DocEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        doc={editingDoc}
        onSave={loadDocuments}
      />
      
      {/* AI Assistant Modal */}
      <AIAssistantModal
        isOpen={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        onSuccess={loadDocuments}
      />
    </div>
  );
}
